import { useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { z } from "zod/v4";
import { toast } from "@/lib/toast";

import { MendeleyCard } from "@/components/settings/MendeleyCard";
import { ZoteroSourcePanel } from "@/components/settings/ZoteroSourcePanel";
import { SettingsNav } from "@/components/settings/SettingsNav";

const searchSchema = z.object({
  connected: z.string().optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute("/settings/integrations")({
  component: IntegrationsPage,
  validateSearch: (search) => searchSchema.parse(search),
});

function IntegrationsPage() {
  const navigate = useNavigate();
  const { connected, error } = Route.useSearch();

  useEffect(() => {
    if (!connected && !error) return;
    if (connected === "mendeley") {
      toast.success("Mendeley connected — first sync runs within ~5 minutes");
    } else if (error === "mendeley_denied") {
      toast.error("Mendeley connection was cancelled");
    } else if (error) {
      toast.error("Mendeley connection failed — please try again");
    }
    // Clear the one-shot params so refresh/back doesn't re-toast.
    navigate({
      to: "/settings/integrations",
      search: {},
      replace: true,
    });
  }, [connected, error, navigate]);

  const goBack = () => {
    // Deep links have no in-app history entry — fall back to the dashboard.
    if (window.history.length > 1) window.history.back();
    else navigate({ to: "/dashboard" });
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <SettingsNav />
      <header>
        <button
          onClick={goBack}
          className="mb-2 flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-3" /> Back
        </button>
        <h1 className="text-xl font-medium">Integrations</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect external tools that auto-sync content into paperchase.
        </p>
      </header>
      <ZoteroSourcePanel />
      <MendeleyCard />
    </div>
  );
}
